import { WarningOutlined } from '@ant-design/icons'
import { Flex, Select, SelectProps, Skeleton, Space, Typography } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { Api } from '~/core/trpc'
import { DataConnectorClient } from '~/plugins/data-connector/client'
import { Toolbox } from '~/plugins/toolbox/shared'

type Props = {
  id?: string
  value?: string
  initialValues?: string
  providerKey?: string
  providerActionKey?: string
  providerAccountId?: string
  providerAccountScopeValues?: { scope: string }[]
  providerActionArgs?: any
  providerItemsKey?: string
  providerItemLabel?: string
  providerItemValueKey?: string
  providerItemDescription?: string
  formValuesConfiguration?: any
  onChange?: (value: string) => void
} & SelectProps

type QueryArgs = {
  appId: string
  providerKey: string
  providerActionKey: string
  providerAccountId: string
  appSessionId: string
  args: string
}

export const SelectProviderAction: React.FC<Props> = ({
  id,
  value,
  formValuesConfiguration = {},
  initialValues,
  providerKey,
  providerAccountId,
  providerAccountScopeValues = [],
  providerActionKey,
  providerActionArgs,
  providerItemValueKey,
  providerItemsKey,
  providerItemLabel,
  providerItemDescription,
  onChange,
  ...props
}) => {
  const [queryArgs, setQueryArgs] = useState<QueryArgs>()
  const [results, setResults] = useState<{ response: any }>({ response: {} })

  const accountQuery = Api.providerAccount.findUnique.useQuery(
    {
      where: { id: providerAccountId },
      include: {
        scopes: {
          where: {
            scope: {
              in: providerAccountScopeValues.map(scope => scope.scope),
            },
          },
        },
      },
    },
    { enabled: !!providerAccountId && providerAccountScopeValues?.length > 0 },
  )

  const providerAccount = accountQuery.data
  const providerAccountScopes = providerAccount?.scopes

  const isProviderAccountCorrupted = providerAccount?.status === 'CORRUPTED'
  const hasScopeCorrupted = providerAccountScopes?.some(
    scope => scope.status === 'CORRUPTED',
  )

  const isCorrupted = isProviderAccountCorrupted || hasScopeCorrupted

  useEffect(() => {
    accountQuery.refetch()
  }, [providerAccountScopeValues])

  const canFetch = providerAccountId && !isCorrupted

  const runAction = Api.dataConnector.actionRun.useMutation()

  const { providerClient, getAction } = DataConnectorClient.useProvider({
    providerKey,
  })

  const action = useMemo(() => {
    if (providerClient && providerActionKey) {
      return getAction(providerActionKey)
    }
  }, [providerActionKey, providerClient, getAction])

  useEffect(() => {
    if (!providerAccountId) {
      handleChange(null)
      runAction.reset()
      setQueryArgs(null)
    }
  }, [providerAccountId])

  useEffect(() => {
    const appId = formValuesConfiguration['appId']
    const appSessionId = formValuesConfiguration['appSessionId']

    if (
      action &&
      appId &&
      appSessionId &&
      providerAccountId &&
      providerItemsKey &&
      providerItemValueKey
    ) {
      setQueryArgs({
        appId,
        providerKey,
        providerActionKey,
        providerAccountId,
        appSessionId,
        args: providerActionArgs ?? {},
      })
    } else {
      setQueryArgs(null)
    }
  }, [
    action,
    formValuesConfiguration,
    providerAccountId,
    providerItemsKey,
    providerItemValueKey,
  ])

  useEffect(() => {
    if (queryArgs && canFetch) {
      runAction.mutateAsync(queryArgs).then(results => setResults(results))
    }
  }, [JSON.stringify(queryArgs), canFetch])

  const options = useMemo(() => {
    const optionsMerged = props.options ?? []

    try {
      if (results) {
        const items = results?.response?.[providerItemsKey] ?? []

        const optionsFetched = items.map(item => {
          const value = item[providerItemValueKey]

          const title = Toolbox.Token.apply(providerItemLabel ?? '', { item })
          const description = Toolbox.Token.apply(
            providerItemDescription ?? '',
            { item },
          )

          return {
            label: (
              <Flex vertical>
                <Typography.Text ellipsis>
                  {Toolbox.Utility.isDefined(title) ? title : 'Unknown'}
                </Typography.Text>
                {Toolbox.Utility.isDefined(description) && (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: '11px' }}
                    ellipsis
                  >
                    {description}
                  </Typography.Text>
                )}
              </Flex>
            ),
            value,
          }
        })

        optionsMerged.push(...optionsFetched)
      }
    } catch (error) {
      console.error('Unknown Error', error)
    }

    return optionsMerged
  }, [results])

  const handleChange = (valueUpdated: string) => {
    onChange(valueUpdated)
  }

  if (isCorrupted) {
    return (
      <span id={id}>
        {value && (
          <Select
            value={value}
            style={{ height: '44px' }}
            disabled
            options={[{ value, label: value }]}
          />
        )}
        <Typography.Text type="warning">
          <Space>
            <WarningOutlined />
            Re-connect your account to continue using this feature
          </Space>
        </Typography.Text>
      </span>
    )
  }

  return (
    <span id={id}>
      {runAction.isLoading || !runAction.isSuccess ? (
        <Skeleton.Input
          block
          active
          style={{ height: '44px', width: '100%' }}
        />
      ) : (
        <Select
          {...props}
          value={value}
          options={options}
          onClear={() => handleChange(null)}
          allowClear
          onChange={handleChange}
          style={{ height: '44px' }}
        />
      )}
    </span>
  )
}
